import { Trash2, ChevronRight } from "lucide-react";
import { Input } from "@/modern/components/ui/input";
import { Textarea } from "@/modern/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modern/components/ui/select";
import { cn } from "@/modern/utils";
import type { GeneratedTag } from "../context/SMMGeneratorContext";
import { TAG_TYPE_COLORS } from "./TagReviewListPanel";

export interface TagReviewEditDraft {
  label: string;
  description: string;
  linkedProblemLabel: string;
}

interface TagReviewDetailPanelProps {
  selectionKey: number | null;
  tag: GeneratedTag | null;
  editDraft: TagReviewEditDraft | null;
  onDraftChange: (patch: Partial<TagReviewEditDraft>) => void;
  showLinkedProblem: boolean;
  problemOptions: { value: string; label: string }[];
  onRemove: () => void;
}

const fieldClass =
  "w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring";

export const TagReviewDetailPanel = ({
  tag,
  editDraft,
  onDraftChange,
  showLinkedProblem,
  problemOptions,
  onRemove,
}: TagReviewDetailPanelProps) => {
  return (
    <div className="flex w-[45%] min-w-0 shrink-0 flex-col h-full min-h-0">
      {tag && editDraft ? (
        <div className="flex flex-col h-full min-h-0">
          <div className="flex items-center justify-between px-5 py-4 shrink-0 border-b border-border">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Edit tag</h3>
              <span
                className={cn(
                  "inline-block mt-1 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wide",
                  TAG_TYPE_COLORS[tag.type] ?? "bg-muted text-muted-foreground",
                )}
              >
                {tag.type}
              </span>
            </div>
            <button
              type="button"
              onClick={onRemove}
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 transition-colors"
            >
              <Trash2 size={13} />
              Remove
            </button>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-4">
            <div className="space-y-1.5">
              <label htmlFor="tag-review-title" className="text-xs font-medium text-muted-foreground">
                Title <span className="text-destructive">*</span>
              </label>
              <Input
                id="tag-review-title"
                value={editDraft.label}
                onChange={(e) => onDraftChange({ label: e.target.value })}
                className={cn(fieldClass, "h-auto", !editDraft.label.trim() && "border-destructive focus-visible:ring-destructive")}
              />
              {!editDraft.label.trim() && (
                <p className="text-[11px] text-destructive">Title is required.</p>
              )}
            </div>

            <div className="space-y-1.5">
              <label htmlFor="tag-review-description" className="text-xs font-medium text-muted-foreground">
                Description
              </label>
              <Textarea
                id="tag-review-description"
                value={editDraft.description}
                onChange={(e) => onDraftChange({ description: e.target.value })}
                rows={4}
                className={cn(fieldClass, "resize-none min-h-[96px]")}
                placeholder="Optional description"
              />
            </div>

            {showLinkedProblem ? (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">
                  Linked problem <span className="text-destructive">*</span>
                </span>
                <Select
                  value={editDraft.linkedProblemLabel || undefined}
                  onValueChange={(v) => onDraftChange({ linkedProblemLabel: v })}
                >
                  <SelectTrigger className={cn(fieldClass, "h-auto py-2", !editDraft.linkedProblemLabel && "border-destructive focus-visible:ring-destructive")}>
                    <SelectValue placeholder="Select a problem" />
                  </SelectTrigger>
                  <SelectContent>
                    {problemOptions.map((p) => (
                      <SelectItem key={p.value} value={p.value}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!editDraft.linkedProblemLabel && (
                  <p className="text-[11px] text-destructive">Linked problem is required for solutions.</p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center min-h-[200px]">
          <div className="rounded-full bg-muted p-3 mb-3">
            <ChevronRight size={20} className="text-muted-foreground" />
          </div>
          <p className="text-sm font-medium text-muted-foreground">Select a tag to edit</p>
          <p className="text-xs text-muted-foreground/60 mt-1 max-w-[240px] leading-relaxed">
            Click any tag on the left to view and edit its details.
          </p>
        </div>
      )}
    </div>
  );
};
